import type {
  AlertAction,
  AlertCondition,
  AlertFilter,
  AlertRule,
  AlertRuleInput,
  Integration,
  Level,
  MatchOp,
} from '@outpost/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ACTION_TYPES,
  CONDITION_TYPES,
  FILTER_OPS,
  FILTER_TYPES,
  LEVELS,
  actionIntegrationKind,
  comparison,
  describeCondition,
  describeFilter,
  emptyRule,
  newAction,
  newCondition,
  newFilter,
  toInput,
  validateRule,
  type ActionType,
  type ConditionType,
  type FilterType,
} from '@/lib/alert-rules';
import { useAuth } from '@/lib/auth';

/**
 * Create or replace one alert rule.
 *
 * Mount it only while it is open, keyed by the rule being edited: the draft is
 * plain state, so remounting is what resets it. Saving is a full replacement —
 * `PUT` revalidates the whole rule server-side — which is why even the fields
 * this form does not show, like `is_active`, are carried through untouched.
 */
export function AlertRuleDialog({
  projectId,
  orgId,
  rule,
  integrations,
  onClose,
}: {
  projectId: string;
  /** The project's organization, for the link to its integrations. */
  orgId: string | undefined;
  /** The rule being edited, or undefined to create one. */
  rule?: AlertRule;
  integrations: Integration[];
  onClose: () => void;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  const initial = rule ? toInput(rule) : emptyRule();
  const [name, setName] = useState(initial.name);
  const [actionMatch, setActionMatch] = useState(initial.action_match);
  const [frequency, setFrequency] = useState<number>(initial.frequency_minutes);
  const conditions = useRows(initial.conditions);
  const filters = useRows(initial.filters);
  const actions = useRows(initial.actions);
  const [attempted, setAttempted] = useState(false);

  const draft: AlertRuleInput = {
    ...initial,
    name,
    action_match: actionMatch,
    frequency_minutes: frequency,
    conditions: conditions.values,
    filters: filters.values,
    actions: actions.values,
  };

  const problems = validateRule(draft, integrations);

  const save = useMutation({
    mutationFn: () =>
      rule
        ? client.updateAlertRule(projectId, rule.id, draft)
        : client.createAlertRule(projectId, draft),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alert-rules', projectId] });
      onClose();
    },
  });

  const submit = () => {
    setAttempted(true);
    if (problems.length === 0) save.mutate();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit alert rule' : 'New alert rule'}</DialogTitle>
          <DialogDescription>
            A rule notifies when its conditions match, its filters all pass, and it has not already
            said so about this issue recently.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="rule-name">Name</Label>
          <Input
            id="rule-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Page on new production errors"
          />
        </div>

        <Section
          title="Conditions"
          description="What has to happen. Conditions describe a change, never steady state."
          onAdd={() => conditions.add(newCondition('new_issue'))}
          addLabel="Add condition"
          heading={
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              Fire when
              <Select
                value={actionMatch}
                onValueChange={(value) => setActionMatch(value as 'all' | 'any')}
              >
                <SelectTrigger className="h-8 w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">all</SelectItem>
                  <SelectItem value="any">any</SelectItem>
                </SelectContent>
              </Select>
              of these happen
            </div>
          }
        >
          {conditions.rows.map((row) => (
            <FieldRow
              key={row.id}
              onRemove={() => conditions.remove(row.id)}
              summary={`Fires when ${describeCondition(row.value)}.`}
            >
              <ConditionFields
                condition={row.value}
                onChange={(value) => conditions.update(row.id, value)}
              />
            </FieldRow>
          ))}
        </Section>

        <Section
          title="Filters"
          description="Optional. Every filter must pass, so each one narrows the rule further."
          onAdd={() => filters.add(newFilter('environment'))}
          addLabel="Add filter"
        >
          {filters.rows.map((row) => (
            <FieldRow
              key={row.id}
              onRemove={() => filters.remove(row.id)}
              summary={`Only while ${describeFilter(row.value)}.`}
            >
              <FilterFields
                filter={row.value}
                onChange={(value) => filters.update(row.id, value)}
              />
            </FieldRow>
          ))}
        </Section>

        <Section
          title="Actions"
          description="Where the notification goes when the rule fires."
          onAdd={() => actions.add(newAction('email'))}
          addLabel="Add action"
        >
          {actions.rows.map((row) => (
            <FieldRow key={row.id} onRemove={() => actions.remove(row.id)}>
              <ActionFields
                action={row.value}
                integrations={integrations}
                orgId={orgId}
                onChange={(value) => actions.update(row.id, value)}
              />
            </FieldRow>
          ))}
        </Section>

        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Label htmlFor="rule-frequency" className="text-muted-foreground">
            Notify at most once every
          </Label>
          <NumberField
            id="rule-frequency"
            value={frequency}
            onChange={setFrequency}
            className="w-20"
          />
          minutes per issue
        </div>

        {attempted && problems.length > 0 && (
          <ul className="space-y-1 text-sm text-destructive">
            {[...new Set(problems)].map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}
        {save.error && <p className="text-sm text-destructive">{(save.error as Error).message}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || (attempted && problems.length > 0)}>
            {rule ? 'Save changes' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- layout -----------------------------------------------------------------

function Section({
  title,
  description,
  heading,
  children,
  onAdd,
  addLabel,
}: {
  title: string;
  description: string;
  heading?: ReactNode;
  children: ReactNode;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <section className="space-y-2 rounded-md border border-border p-3">
      <div>
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {heading}
      <div className="space-y-2">{children}</div>
      <Button variant="outline" size="sm" onClick={onAdd}>
        <Plus />
        {addLabel}
      </Button>
    </section>
  );
}

/** One condition, filter or action: its fields, and the way to remove it. */
function FieldRow({
  children,
  summary,
  onRemove,
}: {
  children: ReactNode;
  /** The same prose the rule list shows, so the effect of a field is visible. */
  summary?: string;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-1 rounded-md bg-muted p-2">
      {/* Fields keep their widths where there is room and give way on a phone. */}
      <div className="flex flex-wrap items-center gap-2 [&>*]:max-w-full">
        {children}
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto size-7"
          onClick={onRemove}
          title="Remove"
        >
          <X />
          <span className="sr-only">Remove</span>
        </Button>
      </div>
      {summary && <p className="text-xs text-muted-foreground">{summary}</p>}
    </div>
  );
}

// --- fields -----------------------------------------------------------------

function ConditionFields({
  condition,
  onChange,
}: {
  condition: AlertCondition;
  onChange: (condition: AlertCondition) => void;
}) {
  return (
    <>
      <Select
        value={condition.type}
        onValueChange={(value) => onChange(newCondition(value as ConditionType))}
      >
        <SelectTrigger className="h-8 w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONDITION_TYPES.map((type) => (
            <SelectItem key={type} value={type}>
              {CONDITION_LABELS[type]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {condition.type === 'event_frequency' && (
        <>
          <span className="text-sm text-muted-foreground">more than</span>
          <NumberField
            value={condition.value}
            onChange={(value) => onChange({ ...condition, value })}
            className="w-24"
            label="Event count"
          />
          <span className="text-sm text-muted-foreground">events in</span>
          <NumberField
            value={condition.interval_minutes}
            onChange={(interval_minutes) => onChange({ ...condition, interval_minutes })}
            className="w-20"
            label="Interval in minutes"
          />
          <span className="text-sm text-muted-foreground">minutes</span>
        </>
      )}

      {condition.type === 'user_frequency' && (
        <>
          <span className="text-sm text-muted-foreground">more than</span>
          <NumberField
            value={condition.value}
            onChange={(value) => onChange({ ...condition, value })}
            className="w-24"
            label="Number of users"
          />
          <span className="text-sm text-muted-foreground">users</span>
        </>
      )}
    </>
  );
}

function FilterFields({
  filter,
  onChange,
}: {
  filter: AlertFilter;
  onChange: (filter: AlertFilter) => void;
}) {
  return (
    <>
      <Select
        value={filter.type}
        onValueChange={(value) => onChange(newFilter(value as FilterType))}
      >
        <SelectTrigger className="h-8 w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FILTER_TYPES.map((type) => (
            <SelectItem key={type} value={type}>
              {FILTER_LABELS[type]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {filter.type === 'tag' && (
        <Input
          className="h-8 w-40"
          value={filter.key}
          onChange={(event) => onChange({ ...filter, key: event.target.value })}
          placeholder="browser.name"
          aria-label="Tag key"
        />
      )}

      {filter.type !== 'issue_age_under' && (
        <Select
          value={filter.op}
          onValueChange={(op) => onChange({ ...filter, op: op as MatchOp })}
        >
          <SelectTrigger className="h-8 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTER_OPS[filter.type].map((op) => (
              <SelectItem key={op} value={op}>
                {comparison(op)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {filter.type === 'level' && (
        <Select
          value={filter.value}
          onValueChange={(value) => onChange({ ...filter, value: value as Level })}
        >
          <SelectTrigger className="h-8 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LEVELS.map((level) => (
              <SelectItem key={level} value={level}>
                {level}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {(filter.type === 'environment' || filter.type === 'release' || filter.type === 'tag') && (
        <Input
          className="h-8 w-44"
          value={filter.value}
          onChange={(event) => onChange({ ...filter, value: event.target.value })}
          placeholder={filter.type === 'release' ? '1.4.2' : 'production'}
          aria-label="Filter value"
        />
      )}

      {filter.type === 'issue_age_under' && (
        <>
          <span className="text-sm text-muted-foreground">younger than</span>
          <NumberField
            value={filter.minutes}
            onChange={(minutes) => onChange({ ...filter, minutes })}
            className="w-20"
            label="Issue age in minutes"
          />
          <span className="text-sm text-muted-foreground">minutes</span>
        </>
      )}
    </>
  );
}

function ActionFields({
  action,
  integrations,
  orgId,
  onChange,
}: {
  action: AlertAction;
  integrations: Integration[];
  orgId: string | undefined;
  onChange: (action: AlertAction) => void;
}) {
  const kind = actionIntegrationKind(action.type);
  const available = integrations.filter(
    (integration) => integration.kind === kind && integration.is_active,
  );

  return (
    <>
      <Select
        value={action.type}
        onValueChange={(value) => onChange(newAction(value as ActionType))}
      >
        <SelectTrigger className="h-8 w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ACTION_TYPES.map((type) => (
            <SelectItem key={type} value={type}>
              {ACTION_LABELS[type]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {action.type === 'email' ? (
        <EmailTargets
          targets={action.targets}
          onChange={(targets) => onChange({ ...action, targets })}
        />
      ) : available.length === 0 ? (
        // The URL an integration holds is a credential, so it is configured
        // once for the organization rather than typed into every rule.
        <p className="text-sm text-muted-foreground">
          No {action.type} integration yet.{' '}
          {orgId && (
            <Link
              className="text-brand underline-offset-4 hover:underline"
              to={`/organizations/${orgId}/settings`}
            >
              Add one in the organization settings
            </Link>
          )}
        </p>
      ) : (
        <Select
          value={action.integration_id}
          onValueChange={(integration_id) => onChange({ ...action, integration_id })}
        >
          <SelectTrigger className="h-8 w-64">
            <SelectValue placeholder="Choose an integration" />
          </SelectTrigger>
          <SelectContent>
            {available.map((integration) => (
              <SelectItem key={integration.id} value={integration.id}>
                {integration.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </>
  );
}

/**
 * Recipients as one comma-separated field.
 *
 * The text is held locally rather than derived from the parsed list, so typing
 * a separator does not make the character vanish under the cursor.
 */
function EmailTargets({
  targets,
  onChange,
}: {
  targets: string[];
  onChange: (targets: string[]) => void;
}) {
  const [text, setText] = useState(targets.join(', '));

  return (
    <Input
      className="h-8 w-72"
      value={text}
      aria-label="Email recipients"
      placeholder="oncall@example.com, team@example.com"
      onChange={(event) => {
        setText(event.target.value);
        onChange(
          event.target.value
            .split(',')
            .map((target) => target.trim())
            .filter(Boolean),
        );
      }}
    />
  );
}

/**
 * A number field that can be empty while being edited.
 *
 * Coercing an empty input straight to a number would rewrite it as `0` between
 * keystrokes; `NaN` keeps the field clearable and fails validation until it
 * holds a number again.
 */
function NumberField({
  value,
  onChange,
  className,
  id,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  id?: string;
  /** Names the field for a screen reader, which cannot read it in context. */
  label?: string;
}) {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : '');

  return (
    <Input
      id={id}
      aria-label={label}
      type="number"
      min={1}
      className={`h-8 ${className ?? ''}`}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        onChange(event.target.value === '' ? Number.NaN : Number(event.target.value));
      }}
    />
  );
}

// --- rows -------------------------------------------------------------------

interface Row<T> {
  id: number;
  value: T;
}

let nextRowId = 0;

/**
 * A list of editable rows with stable identities.
 *
 * Keying rows by array index would let a row's local state — the text in a
 * number field, say — survive onto whatever shifts into that index when an
 * earlier row is removed.
 */
function useRows<T>(initial: T[]) {
  const [rows, setRows] = useState<Row<T>[]>(() =>
    initial.map((value) => ({ id: nextRowId++, value })),
  );

  return {
    rows,
    values: rows.map((row) => row.value),
    add: (value: T) => setRows((current) => [...current, { id: nextRowId++, value }]),
    update: (id: number, value: T) =>
      setRows((current) => current.map((row) => (row.id === id ? { ...row, value } : row))),
    remove: (id: number) => setRows((current) => current.filter((row) => row.id !== id)),
  };
}

const CONDITION_LABELS: Record<ConditionType, string> = {
  new_issue: 'A new issue appears',
  issue_regression: 'A resolved issue returns',
  event_frequency: 'Events exceed a threshold',
  user_frequency: 'Affected users exceed a threshold',
  every_event: 'Every event',
};

const FILTER_LABELS: Record<FilterType, string> = {
  level: 'Level',
  environment: 'Environment',
  release: 'Release',
  tag: 'Tag',
  issue_age_under: 'Issue age',
};

const ACTION_LABELS: Record<ActionType, string> = {
  email: 'Email',
  webhook: 'Webhook',
  slack: 'Slack',
};
